<template>
  <div class="anytime-consultation-user-info-tab pa-4">
    <BaseInfoSection
      :user-id="user.id"
      :user-name="user.name"
      :show-user-name="false"
      :user-gender="user.userGender"
      :last-due-date="user.lastDueDate"
      :coupon="displayCoupon"
      :coupon-id="displayCouponId"
    />

    <LegacyUserNoticeSections
      v-if="legacyUserNoticesEnabled"
      :user="user"
      :user-note-editable="answerEditable"
      :can-edit-user-note="canEditNotes"
      @saved="start"
    />
    <NoteSection
      v-else
      title="ユーザーの特記事項"
      :content="specialNote"
      :editable="answerEditable"
      :can-edit="canEditNotes"
      :is-editing="isEditingUserNote"
      :draft="userNoticeDraft"
      :is-saving="userNoteSaving"
      @toggle-edit="toggleUserNoteEdit"
      @update:draft="userNoticeDraft = $event"
      @cancel-edit="cancelUserNoteEdit"
    />

    <ChildInfoSection
      :children="children"
      :show-child-name="false"
      :loading="childrenLoading"
      :is-editing="isEditingChildren"
      :drafts="childNoticeDrafts"
      :can-edit="canEditNotes"
      :is-saving="childNoteSaving"
      @start-edit="startChildNoteEdit"
      @save-edit="saveChildNoteEdit"
      @update:drafts="childNoticeDrafts = $event"
      @cancel-edit="cancelChildNoteEdit"
    />

    <ConsultationHistorySection
      :histories="consultationHistoriesWithoutChildNames"
      :show-child-name="false"
      @open="openConsultation"
    />

    <MunicipalCooperationHistorySection :histories="municipalCooperationHistories" />

    <FrequentUseResponseHistorySection :histories="frequentUseResponseHistories" />

    <HighRiskReportHistorySection :histories="highRiskReportHistories" />

    <RiskAssessmentHistorySection
      :histories="riskAssessmentHistories"
      @open="openRiskAssessment"
    />

    <EpdsHistorySection
      :histories="epdsHistories"
      @open="openEpds"
    />

    <RiskAssessmentViewDialog
      v-if="viewingRiskAssessmentId"
      :assessment-id="viewingRiskAssessmentId"
      @close="viewingRiskAssessmentId = null"
    />
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { RiskAssessmentViewDialog } from "@/vue/features/risk-assessment-input";
import { useStore } from "vuex";
import BaseInfoSection from "@/vue/widgets/user-info-tab/ui/BaseInfoSection.vue";
import NoteSection from "@/vue/widgets/user-info-tab/ui/NoteSection.vue";
import LegacyUserNoticeSections from "@/vue/widgets/user-info-tab/ui/LegacyUserNoticeSections.vue";
import { FEATURE_FLAGS } from "@/vue/shared/utils/featureFlags";
import ChildInfoSection from "@/vue/widgets/user-info-tab/ui/ChildInfoSection.vue";
import ConsultationHistorySection from "@/vue/widgets/user-info-tab/ui/ConsultationHistorySection.vue";
import EpdsHistorySection from "@/vue/widgets/user-info-tab/ui/EpdsHistorySection.vue";
import MunicipalCooperationHistorySection from "@/vue/widgets/user-info-tab/ui/MunicipalCooperationHistorySection.vue";
import FrequentUseResponseHistorySection from "@/vue/widgets/user-info-tab/ui/FrequentUseResponseHistorySection.vue";
import HighRiskReportHistorySection from "@/vue/widgets/user-info-tab/ui/HighRiskReportHistorySection.vue";
import RiskAssessmentHistorySection from "@/vue/widgets/user-info-tab/ui/RiskAssessmentHistorySection.vue";
import { useUserInfoTabData } from "@/vue/composables/useUserInfoTabData";
import childApi from "@/vue/api/children";
import specialNotesApi from "@/vue/api/users/special_notes";
import { OPTIMISTIC_LOCK_ALERT_MESSAGE } from "@/vue/entities/face-sheet";

const props = defineProps({
  consultation: { type: Object, required: true },
  // いつでも相談の回答が編集可能な状態（旧AnytimeConsultationAddon.vueの`answer.editable`相当）かどうか。
  // trueの間のみ、特記事項（ユーザー/子）を編集可能にする
  answerEditable: { type: Boolean, default: false },
});

const store = useStore();

const {
  user, specialNote, specialNoteRevisionId, children, childrenLoading, consultationHistories, epdsHistories,
  municipalCooperationHistories, frequentUseResponseHistories, highRiskReportHistories, riskAssessmentHistories,
  start, clear
} = useUserInfoTabData({
  getUserId: () => props.consultation.userId,
});

const viewingRiskAssessmentId = ref(null);

// ユーザー情報の取得はこのタブ自身が担当する。親のタブ同期に依存すると、
// 初回マウント時のref確定タイミングによって取得が抜けるため。
watch(
  () => props.consultation.userId,
  (userId, previousUserId) => {
    // 前ユーザーのアセスメント閲覧ダイアログを持ち越さない
    viewingRiskAssessmentId.value = null;
    if (userId == null) {
      clear();
      return;
    }
    if (userId !== previousUserId) start();
  },
  { immediate: true },
);

// ユーザー・子の特記事項は回答を編集できる相談員にも許可する。
const canEditNotes = computed(() => props.answerEditable);

const legacyUserNoticesEnabled = computed(() => store.getters.isFeatureEnabled?.(FEATURE_FLAGS.legacyUserNotices) === true);

const isEditingUserNote = ref(false);
const userNoticeDraft = ref("");
const userNoteSaving = ref(false);
const isEditingChildren = ref(false);
const childNoticeDrafts = ref({});
const childNoteSaving = ref(false);

// サイドバーは相談を切り替えてもこのタブを使い回すため、前ユーザーの下書きを別ユーザーへ保存させない
watch(() => user.value.id, () => {
  isEditingUserNote.value = false;
  userNoticeDraft.value = "";
  userNoteSaving.value = false;
});

const displayCoupon = computed(() => {
  if (user.value.coupon && user.value.coupon !== "") {
    return user.value.coupon;
  }
  return "-";
});

const displayCouponId = computed(() => {
  if (user.value.couponId !== null && user.value.couponId !== undefined && user.value.couponId !== "") {
    return user.value.couponId;
  }
  return "";
});

const consultationHistoriesWithoutChildNames = computed(() => consultationHistories.value.map((history) => ({
  ...history,
  childName: null,
})));

// AnytimeConsultation.vueのsaveDraft/completeAnswerは、Vuexのuser/child（store.getters.user/child）を
// 参照して既存のupdateUser/updateChildを呼び出す。このタブは独自にユーザー/子を取得しており別オブジェクトの
// ため、ここでの保存内容がVuex側に反映されないまま放置すると、次回の下書き保存/送信予約時に
// Vuex側の古い値でこのタブの編集内容が上書きされてしまう。それを防ぐため、保存成功時はVuex側にも同じ値を反映する。
const syncVuexChild = (childId, patch) => {
  const vuexChild = store.getters.child;
  if (vuexChild && vuexChild.id != null && vuexChild.id === childId) {
    Object.assign(vuexChild, patch);
  }
};

const updateChild = (params) => new Promise((resolve, reject) => {
  childApi.updateChild(params, resolve, reject);
});

const toggleUserNoteEdit = async () => {
  if (!canEditNotes.value) return;

  if (!isEditingUserNote.value) {
    userNoticeDraft.value = specialNote.value;
    isEditingUserNote.value = true;
    return;
  }

  const userId = user.value.id;
  // 保存中に別ユーザーへ切り替わった場合、完了後の状態更新を切替後のユーザーに反映しない
  const isStale = () => user.value.id !== userId;

  userNoteSaving.value = true;
  try {
    await specialNotesApi.saveSpecialNote(String(userId), userNoticeDraft.value, specialNoteRevisionId.value);
  } catch (e) {
    if (isStale()) return;
    if (e?.response?.status === 409) {
      alert(OPTIMISTIC_LOCK_ALERT_MESSAGE);
      isEditingUserNote.value = false;
      userNoteSaving.value = false;
      await start();
      return;
    }
    throw e;
  } finally {
    if (!isStale()) userNoteSaving.value = false;
  }

  if (isStale()) return;
  isEditingUserNote.value = false;
  await start();
};

const cancelUserNoteEdit = () => {
  isEditingUserNote.value = false;
  userNoticeDraft.value = "";
};

const startChildNoteEdit = () => {
  if (!canEditNotes.value) return;
  const drafts = {};
  children.value.forEach((c) => {
    drafts[c.id] = c.notice || "";
  });
  childNoticeDrafts.value = drafts;
  isEditingChildren.value = true;
};

const cancelChildNoteEdit = () => {
  isEditingChildren.value = false;
  childNoticeDrafts.value = {};
};

const saveChildNoteEdit = async () => {
  if (!isEditingChildren.value) return;

  // 変更のあった子だけPUT
  const changed = children.value.filter((c) => {
    const draft = childNoticeDrafts.value[c.id] ?? c.notice ?? "";
    return draft !== (c.notice || "");
  });

  if (changed.length === 0) {
    isEditingChildren.value = false;
    childNoticeDrafts.value = {};
    return;
  }

  childNoteSaving.value = true;
  try {
    for (const child of changed) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await updateChild({ id: child.id, notice: childNoticeDrafts.value[child.id], lockVersion: child.lockVersion });
        syncVuexChild(child.id, { notice: childNoticeDrafts.value[child.id] });
      } catch (e) {
        if (e?.response?.status === 409) {
          alert(OPTIMISTIC_LOCK_ALERT_MESSAGE);
          childNoteSaving.value = false;
          isEditingChildren.value = false;
          childNoticeDrafts.value = {};
          await start();
          return;
        }
        throw e;
      }
    }
  } finally {
    childNoteSaving.value = false;
  }

  isEditingChildren.value = false;
  childNoticeDrafts.value = {};
  await start();
};

const openConsultation = (history) => {
  if (history.sourceType === "daytime") {
    window.open(`/daytime_advices_sheets/${history.id}`, "", "noopener,noreferrer,width=480,height=640");
    return;
  }
  if (history.sourceType === "anytime") {
    window.open(`/anytime_consultations/view/${history.id}`, "", "noopener,noreferrer,width=480,height=640");
    return;
  }
  if (history.reservationId) {
    window.open(`/reservations/${history.reservationId}`, "", "noopener,noreferrer,width=480,height=640");
  }
};

const openEpds = (id) => {
  window.open(`/epds/${id}`, "", "noopener,noreferrer,width=480,height=640");
};

const openRiskAssessment = (history) => {
  viewingRiskAssessmentId.value = history.id;
};

defineExpose({
  start,
  clear,
});
</script>

<style lang="scss" scoped>
.anytime-consultation-user-info-tab {
  background-color: #f7f7f7;
  min-height: calc(100vh - 150px);
}
</style>
